import { useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { ShoppingBag, X, Copy, Download, Sparkles, Check, BadgeCheck } from "lucide-react";

/**
 * Poshmark Copy-Paste Generator modal.
 *
 * Same pattern as InstagramPrepModal — Poshmark has no public listing API for
 * third-party sellers (2026 state). We generate a Poshmark-shaped listing
 * (title ≤80c, structured description, 3 hashtags, category/subcategory hint)
 * and let the seller paste it into the Poshmark web form.
 */
export default function PoshmarkPrepModal({ item, onClose, onPosted }) {
  const [tone, setTone] = useState("boutique");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [zipping, setZipping] = useState(false);
  const [marking, setMarking] = useState(false);

  const generate = async () => {
    setGenerating(true);
    setResult(null);
    try {
      const { data } = await api.post(`/inventory/${item.id}/poshmark/generate`, { tone });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not generate poshmark listing.");
    } finally {
      setGenerating(false);
    }
  };

  const copy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
      toast.success("copied to clipboard.");
    } catch {
      toast.error("could not copy — select manually.");
    }
  };

  const downloadZip = async () => {
    setZipping(true);
    try {
      // Poshmark accepts up to 16 photos — the /instagram/photos-zip endpoint
      // already caps at 24 and is fine to reuse. The zip filename references
      // Maison Pilet, not the channel, so it's channel-agnostic.
      const res = await api.get(`/inventory/${item.id}/instagram/photos-zip?channel=poshmark`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `maison-pilet-${(item.sku || item.id).slice(0, 20)}-photos.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("photos zip downloaded.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not download photos.");
    } finally {
      setZipping(false);
    }
  };

  const markPosted = async () => {
    if (!window.confirm("Mark this piece as posted to Poshmark? It will drop out of the still-to-post queue.")) return;
    setMarking(true);
    try {
      await api.post(`/inventory/${item.id}/poshmark/mark-posted`);
      toast.success("marked as posted to poshmark.");
      onPosted && onPosted();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not mark as posted.");
    } finally {
      setMarking(false);
    }
  };

  const tones = [
    { key: "boutique", label: "boutique · editorial", hint: "warm brand-forward voice (default)" },
    { key: "luxury", label: "luxury · refined", hint: "condition + provenance lead" },
    { key: "casual", label: "casual · playful", hint: "poshmark's default vibe" },
    { key: "sustainable", label: "sustainable", hint: "second-life story" },
    { key: "promotional", label: "promotional", hint: "ready-to-ship, bundle-friendly" },
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-charcoal/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
      onClick={onClose}
      data-testid="posh-prep-modal-overlay"
    >
      <div
        className="bg-ivory border border-taupe/50 max-w-2xl w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="posh-prep-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-taupe/40 px-6 py-4">
          <div>
            <h2 className="font-serif text-2xl lowercase text-charcoal flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-brass" /> prep for poshmark
            </h2>
            <p className="text-xs text-espresso mt-1 lowercase">
              paste-ready listing · title / description / hashtags / category · zip of photos.
            </p>
          </div>
          <button onClick={onClose} data-testid="posh-prep-close-btn" className="text-taupe hover:text-charcoal p-1 -m-1" aria-label="close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6">
          {/* Item summary */}
          <div className="flex items-center gap-4 pb-4 border-b border-taupe/30">
            {item.photos?.[0] && (
              <div className="w-16 h-16 border border-taupe/30 overflow-hidden flex-shrink-0 bg-linen">
                <img src={fileUrl(item.photos[0])} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-serif text-lg lowercase text-charcoal truncate">{item.title || "(untitled)"}</p>
              <p className="text-[0.7rem] uppercase tracking-widest text-taupe mt-0.5">
                {item.brand || "—"} · {item.size || "—"} · {(item.photos || []).length} photo{(item.photos || []).length === 1 ? "" : "s"}
              </p>
            </div>
            {item.posted_to_poshmark && (
              <span className="text-[0.6rem] uppercase tracking-widest text-emerald-700 flex items-center gap-1">
                <BadgeCheck className="w-3 h-3" /> already posted
              </span>
            )}
          </div>

          {/* Tone picker */}
          <div>
            <p className="editorial-label mb-2">tone</p>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="posh-tone-picker">
              {tones.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTone(t.key)}
                  data-testid={`posh-tone-${t.key}`}
                  className={`text-left px-3 py-2 border transition-colors ${
                    tone === t.key
                      ? "border-brass bg-brass/10 text-charcoal"
                      : "border-taupe/40 hover:border-taupe text-espresso"
                  }`}
                >
                  <p className="text-xs lowercase tracking-widest font-medium">{t.label}</p>
                  <p className="text-[0.65rem] text-taupe mt-0.5">{t.hint}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Generate */}
          <button
            onClick={generate}
            disabled={generating}
            data-testid="posh-generate-btn"
            className="w-full bg-charcoal hover:bg-brass text-ivory px-5 py-3 text-xs tracking-[0.25em] uppercase flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" />
            {generating ? "writing…" : result ? "regenerate" : "generate listing"}
          </button>

          {/* Result */}
          {result && (
            <div className="space-y-4" data-testid="posh-result-panel">
              {/* Title */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="editorial-label">listing title <span className="text-taupe">({result.listing_title.length}/80)</span></p>
                  <button onClick={() => copy(result.listing_title, "title")} className="text-xs text-brass hover:text-charcoal flex items-center gap-1" data-testid="posh-copy-title-btn">
                    {copiedField === "title" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copiedField === "title" ? "copied" : "copy"}
                  </button>
                </div>
                <input readOnly value={result.listing_title} data-testid="posh-title-output" className="w-full border border-taupe/40 bg-white p-3 text-sm text-charcoal font-serif" />
              </div>

              {/* Category / size hint / condition */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="editorial-label mb-1">category</p>
                  <p className="text-sm text-charcoal border border-taupe/30 bg-linen px-3 py-2" data-testid="posh-category">{result.poshmark_category || "—"} / {result.poshmark_subcategory || "—"}</p>
                </div>
                <div>
                  <p className="editorial-label mb-1">size · condition</p>
                  <p className="text-sm text-charcoal border border-taupe/30 bg-linen px-3 py-2" data-testid="posh-size-condition">{result.poshmark_size_hint || "—"} · {result.condition_recommendation || "—"}</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="editorial-label">description <span className="text-taupe">({result.description.length}/1500)</span></p>
                  <button onClick={() => copy(result.description, "desc")} className="text-xs text-brass hover:text-charcoal flex items-center gap-1" data-testid="posh-copy-desc-btn">
                    {copiedField === "desc" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copiedField === "desc" ? "copied" : "copy"}
                  </button>
                </div>
                <textarea readOnly value={result.description} rows={7} data-testid="posh-desc-output" className="w-full border border-taupe/40 bg-white p-3 text-sm text-charcoal font-serif leading-relaxed resize-y" />
              </div>

              {/* Hashtags */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="editorial-label">hashtags ({result.hashtags.length}/3)</p>
                  <button onClick={() => copy(result.hashtags.map((h) => `#${h}`).join(" "), "hashtags")} className="text-xs text-brass hover:text-charcoal flex items-center gap-1" data-testid="posh-copy-hashtags-btn">
                    {copiedField === "hashtags" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copiedField === "hashtags" ? "copied" : "copy"}
                  </button>
                </div>
                <div className="border border-taupe/40 bg-white p-3 text-xs text-espresso flex flex-wrap gap-1.5" data-testid="posh-hashtags-output">
                  {result.hashtags.map((h) => (
                    <span key={h} className="bg-linen border border-taupe/30 px-2 py-0.5 rounded-sm">#{h}</span>
                  ))}
                </div>
              </div>

              {/* Full paste-ready */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="editorial-label">full description (paste-ready)</p>
                  <button onClick={() => copy(result.full_description, "full")} className="text-xs text-brass hover:text-charcoal flex items-center gap-1" data-testid="posh-copy-full-btn">
                    {copiedField === "full" ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copiedField === "full" ? "copied" : "copy all"}
                  </button>
                </div>
                <p className="text-[0.65rem] text-taupe italic">description + hashtags · ready to paste into poshmark&apos;s description field.</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-taupe/40 px-6 py-4 flex flex-wrap gap-3 justify-between items-center bg-linen/40">
          <button
            onClick={downloadZip}
            disabled={zipping || !(item.photos || []).length}
            data-testid="posh-download-zip-btn"
            className="border border-charcoal text-charcoal hover:bg-charcoal hover:text-ivory px-4 py-2.5 text-xs tracking-[0.25em] uppercase flex items-center gap-2 disabled:opacity-50"
          >
            <Download className="w-3 h-3" />
            {zipping ? "packing…" : "download photos (zip)"}
          </button>
          <button
            onClick={markPosted}
            disabled={marking || item.posted_to_poshmark}
            data-testid="posh-mark-posted-btn"
            className="bg-brass hover:bg-espresso text-ivory px-4 py-2.5 text-xs tracking-[0.25em] uppercase flex items-center gap-2 disabled:opacity-50"
          >
            <BadgeCheck className="w-3 h-3" />
            {item.posted_to_poshmark ? "already marked" : marking ? "marking…" : "mark as posted"}
          </button>
        </div>
      </div>
    </div>
  );
}
